/* Collection pagination and product-to-list history restoration. Search has its own owner. */
(() => {
  'use strict';
  if (window.FAFCollectionListing) return;

  const gridSelector = '.collection-grid-layout #ProductGridContainer #product-grid.load-more-grid';
  const storageKey = 'faf-collection-scroll-v1';
  const lifetime = 60 * 60 * 1000;
  const restoreLimit = 50;
  const integer = (value, fallback = 1) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : fallback;

  class CollectionListing {
    constructor() {
      this.grid = null;
      this.sequence = 0;
      this.restoreSequence = 0;
      this.restoring = false;
      this.loading = false;
      this.hidden = false;
      this.waitingRender = false;
      this.autoBlocked = false;
      this.observer = null;
      this.request = null;
      this.scrollTimer = null;
      this.pendingRestore = null;
      this.departureView = null;
      this.positionGuard = null;
      this.releaseScrollMode = null;
      const initialSaved = this.savedView();
      this.mount();
      if (initialSaved && this.grid) this.restore(initialSaved);
      else this.savePosition();

      const captureProduct = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const product = target?.closest('a[href*="/products/"]');
        if (product && this.grid?.contains(product)) {
          this.interruptRestore();
          this.savePosition();
        }
      };
      ['click', 'auxclick', 'contextmenu'].forEach(type => document.addEventListener(type, captureProduct, true));
      window.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.savePosition(), 350);
      }, { passive: true });
      ['pointerdown', 'touchstart', 'wheel'].forEach(type => document.addEventListener(type, event => this.interruptRestore(event), { passive: true, capture: true }));
      document.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab', 'Escape'].includes(event.key)) this.interruptRestore();
      }, true);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.savePosition();
      });
      window.addEventListener('pagehide', () => {
        this.savePosition();
        this.departureView = this.savedView();
        this.hidden = true;
        this.cancel();
      });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.hidden = false;
        // Keep the retained DOM. Correct only if native restoration misses the
        // saved anchor; browsers finish that work after pageshow/popstate.
        if (this.hasCurrentView()) {
          this.loading = false;
          this.updateControls();
          const saved = this.savedView() || this.departureView;
          if (saved) this.restore(saved);
          else this.observe();
        }
      });
    }

    isActive() { return /\/collections\//.test(location.pathname) && Boolean(document.querySelector(gridSelector)); }

    signature() {
      const params = new URLSearchParams(location.search);
      params.delete('section_id');
      params.sort();
      return [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang, location.pathname, params.toString()].join('|');
    }

    hasCurrentView() { return Boolean(this.grid?.isConnected && !this.waitingRender && this.viewSignature === this.signature()); }

    storedViews() {
      try {
        const entries = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        return Array.isArray(entries) ? entries.filter(item => item && Number.isFinite(item.savedAt) && Date.now() - item.savedAt < lifetime).slice(-8) : [];
      } catch { return []; }
    }

    savedView() {
      const state = history.state?.fafCollectionScroll;
      if (!state || typeof state.key !== 'string') return null;
      const saved = this.storedViews().find(item => item.key === state.key) || state;
      const start = integer(new URLSearchParams(location.search).get('page'));
      if (saved.signature !== this.signature() || saved.startPage !== start || !Number.isSafeInteger(saved.loadedPage) ||
        saved.loadedPage < start || !Number.isFinite(saved.scrollY) || saved.scrollY < 0 || !Number.isFinite(saved.savedAt) ||
        Date.now() - saved.savedAt >= lifetime) return null;
      return saved;
    }

    cardKey(card) {
      const link = card?.querySelector('a[href*="/products/"]');
      if (!link) return '';
      try {
        const url = new URL(link.getAttribute('href'), location.origin);
        // Swatches can change a variant URL; the product itself remains the anchor.
        return '/products/' + url.pathname.split('/products/')[1];
      } catch { return ''; }
    }

    safeTop() {
      let top = 0;
      document.querySelectorAll('.shopify-section-header, header').forEach(header => {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.bottom < window.innerHeight) top = Math.max(top, rect.bottom + 8);
      });
      return top;
    }

    savePosition() {
      if (!this.hasCurrentView() || this.restoring || this.pendingRestore || this.positionGuard || this.hidden) return;
      const cards = Array.from(this.grid.children);
      const safeTop = this.safeTop();
      const anchor = cards.find(card => card.getBoundingClientRect().bottom > safeTop) || cards[cards.length - 1];
      const rect = anchor?.getBoundingClientRect();
      const saved = {
        key: this.key, signature: this.viewSignature, startPage: this.startPage, loadedPage: this.page,
        scrollY: Math.max(0, window.scrollY), savedAt: Date.now(), viewportWidth: window.innerWidth,
        anchor: anchor ? { key: this.cardKey(anchor), offset: rect.top, height: rect.height } : null,
      };
      // Keep the facet controller's history namespace, including on the original unfiltered entry.
      try { history.replaceState({ ...history.state, searchParams: location.search.slice(1), fafCollectionScroll: saved }, '', location.href); } catch { /* History may be restricted in embedded browsers. */ }
      try {
        const entries = this.storedViews().filter(item => item.key !== saved.key);
        entries.push(saved);
        sessionStorage.setItem(storageKey, JSON.stringify(entries.slice(-8)));
      } catch { /* The in-entry snapshot still works when storage is unavailable. */ }
    }

    cancel() {
      this.stopPositionGuard();
      this.sequence += 1;
      this.restoreSequence += 1;
      this.restoring = false;
      this.request?.abort();
      this.request = null;
      this.loading = false;
      this.observer?.disconnect();
      clearTimeout(this.scrollTimer);
    }

    claimScrollMode() {
      this.stopPositionGuard();
      try {
        const previous = history.scrollRestoration || 'auto';
        history.scrollRestoration = 'manual';
        this.releaseScrollMode = () => {
          try { if (history.scrollRestoration === 'manual') history.scrollRestoration = previous; } catch {}
        };
      } catch {}
    }

    stopPositionGuard() {
      this.positionGuard?.();
      this.positionGuard = null;
      this.releaseScrollMode?.();
      this.releaseScrollMode = null;
    }

    beforeRender() {
      this.savePosition();
      this.cancel();
      this.pendingRestore = null;
      this.waitingRender = true;
    }

    mount() {
      if (!/\/collections\//.test(location.pathname)) return;
      const grid = document.querySelector(gridSelector);
      if (!grid) { this.cancel(); this.grid = null; return; }
      if (this.grid !== grid || this.viewSignature !== this.signature() || this.waitingRender) {
        this.cancel();
        this.grid = grid;
        this.container = grid.closest('#ProductGridContainer');
        this.viewSignature = this.signature();
        this.startPage = integer(new URLSearchParams(location.search).get('page'));
        this.page = integer(this.container.querySelector('[data-current-page]')?.value, this.startPage);
        this.totalPages = integer(this.container.querySelector('[data-total-pages]')?.value, this.page);
        this.key = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        this.autoBlocked = false;
        this.pendingRestore = null;
        this.waitingRender = false;
        this.hidden = false;
      }
      this.container.querySelectorAll('.js-load-more').forEach(button => {
        button.onclick = () => this.loadNext(button, false);
      });
      this.updateControls();
      this.observe();
    }

    updateControls(error = false) {
      if (!this.container?.isConnected) return;
      const done = this.page >= this.totalPages;
      this.container.querySelectorAll('.js-load-more, .js-infinite-scroll').forEach(control => {
        control.hidden = done;
        control.toggleAttribute('disabled', this.loading);
        control.classList.toggle('loading', this.loading);
      });
      const retry = this.container.querySelector('[data-faf-collection-retry]');
      if (retry) retry.hidden = !error || done;
      if (error && !done && !retry) {
        const box = document.createElement('div');
        box.dataset.fafCollectionRetry = '';
        box.className = 'button-wrapper';
        const text = document.createElement('p');
        text.setAttribute('role', 'status');
        text.textContent = this.container.dataset.fafLoadError || 'More products could not load. Please try again.';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary';
        button.textContent = this.container.dataset.fafLoadMoreLabel || 'Load more products';
        button.addEventListener('click', () => this.loadNext(button, false));
        box.append(text, button);
        this.grid.insertAdjacentElement('afterend', box);
      }
    }

    observe() {
      this.observer?.disconnect();
      if (!this.hasCurrentView() || this.hidden || this.loading || this.restoring || this.autoBlocked || this.page >= this.totalPages || !('IntersectionObserver' in window)) return;
      const spinner = this.container.querySelector('.js-infinite-scroll');
      if (!spinner) return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadNext(spinner, false);
      });
      this.observer.observe(spinner);
    }

    async loadNext(button, restoring = false) {
      if (!restoring && this.pendingRestore && !this.restoring) return this.restore(this.pendingRestore);
      if (!this.hasCurrentView() || this.loading || this.hidden || this.page >= this.totalPages || (this.restoring && !restoring)) return false;
      const grid = this.grid;
      const requestId = this.sequence;
      const page = this.page + 1;
      const controller = new AbortController();
      this.request = controller;
      this.loading = true;
      this.observer?.disconnect();
      this.updateControls();
      const current = () => this.grid === grid && grid.isConnected && this.sequence === requestId && !controller.signal.aborted && this.viewSignature === this.signature();
      try {
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.set('page', String(page));
        url.searchParams.delete('phcursor');
        if (grid.dataset.id) url.searchParams.set('section_id', grid.dataset.id);
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Collection request failed');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!current()) return false;
        const incoming = parsed.querySelector(gridSelector);
        if (!incoming || !incoming.children.length) throw new Error('Invalid collection response');
        const cards = Array.from(incoming.children);
        const existing = new Set(Array.from(grid.children).map(card => this.cardKey(card)).filter(Boolean));
        const newCards = cards.filter(card => !existing.has(this.cardKey(card)));
        if (!newCards.length) throw new Error('Repeated collection response');
        grid.append(...newCards);
        // Advance only after success; retries must never skip a page.
        this.page = page;
        const currentInput = this.container.querySelector('[data-current-page]');
        if (currentInput) { currentInput.value = String(page); currentInput.dataset.currentPage = String(page); }
        const incomingContainer = incoming.closest('#ProductGridContainer');
        this.totalPages = integer(incomingContainer?.querySelector('[data-total-pages]')?.value, this.totalPages);
        const nextInput = this.container.querySelector('[data-next-url]');
        if (nextInput) nextInput.value = incomingContainer?.querySelector('[data-next-url]')?.value || '';
        this.autoBlocked = false;
        if (typeof colorSwatches === 'function') colorSwatches();
        this.savePosition();
        document.dispatchEvent(new CustomEvent('faf:collection-page-appended', { detail: { page, root: grid } }));
        return true;
      } catch (error) {
        if (current()) {
          this.autoBlocked = true;
          this.updateControls(true);
        }
        return false;
      } finally {
        if (this.sequence === requestId && this.grid === grid) {
          this.loading = false;
          this.request = null;
          this.updateControls(this.autoBlocked);
          this.observe();
        }
      }
    }

    interruptRestore(event) {
      if (!this.restoring && !this.pendingRestore && !this.positionGuard) return;
      if (!this.restoring && this.pendingRestore && event?.type !== 'wheel' && event?.target instanceof Element &&
        event.target.closest('[data-faf-collection-retry] button, .js-load-more')) return;
      this.pendingRestore = null;
      this.cancel();
      this.updateControls(this.autoBlocked);
      this.savePosition();
      this.observe();
    }

    async restore(saved) {
      if (!saved || !this.hasCurrentView() || saved.signature !== this.viewSignature) return false;
      this.claimScrollMode();
      this.observer?.disconnect();
      this.restoring = true;
      this.pendingRestore = saved;
      this.key = saved.key;
      const token = ++this.restoreSequence;
      const grid = this.grid;
      const current = () => this.restoring && this.restoreSequence === token && this.grid === grid && this.hasCurrentView();
      const target = Math.min(saved.loadedPage, this.totalPages, this.startPage + restoreLimit - 1);
      while (current() && this.page < target) {
        if (!await this.loadNext(null, true)) break;
      }
      if (!current()) return false;
      if (this.page < target) {
        // Keep the original destination intact after a network failure. A retry
        // can finish reconstructing it; only deliberate shopping replaces it.
        this.restoring = false;
        this.stopPositionGuard();
        this.updateControls(true);
        return false;
      }
      // Initial history traversal can finish after DOMContentLoaded. Let its native
      // restoration run first, then correct against the rebuilt product anchor.
      if (document.readyState !== 'complete') {
        await new Promise(resolve => {
          const done = () => { clearTimeout(timer); window.removeEventListener('pageshow', done); resolve(); };
          const timer = setTimeout(done, 1500);
          window.addEventListener('pageshow', done, { once: true });
        });
      }
      const started = performance.now();
      const anchor = saved.anchor?.key ? Array.from(grid.children).find(card => this.cardKey(card) === saved.anchor.key) : null;
      const correctPosition = () => {
        const rect = anchor?.isConnected ? anchor.getBoundingClientRect() : null;
        const safeTop = this.safeTop();
        const reflowed = Number.isFinite(saved.viewportWidth) && Math.abs(saved.viewportWidth - window.innerWidth) > 2 ||
          rect && Number.isFinite(saved.anchor?.height) && Math.abs(saved.anchor.height - rect.height) > 2;
        const offset = rect && Number.isFinite(saved.anchor?.offset) ? saved.anchor.offset : null;
        const visibleOffset = rect && (reflowed || offset + rect.height <= safeTop || offset >= window.innerHeight - 24) ? safeTop : offset;
        const desired = saved.scrollY <= 2 ? 0 : Math.max(0, rect && offset !== null ? window.scrollY + rect.top - visibleOffset : saved.scrollY);
        const available = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        if (Math.abs(window.scrollY - Math.min(desired, available)) > 2) window.scrollTo({ top: desired, behavior: 'instant' });
      };
      // A short settling window covers delayed image/font layout without fighting
      // a shopper: any touch, pointer, wheel, or navigation key cancels immediately.
      while (current() && performance.now() - started < 1200) {
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (!current()) return false;
        correctPosition();
      }
      if (!current()) return false;
      this.restoring = false;
      this.pendingRestore = null;
      this.savePosition();
      const stillCurrent = () => this.restoreSequence === token && this.grid === grid && this.hasCurrentView() && !this.hidden;
      let frame = null;
      const scheduleCorrection = () => {
        if (frame !== null || !stillCurrent()) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (stillCurrent() && this.positionGuard) correctPosition();
        });
      };
      const finish = () => {
        this.stopPositionGuard();
        this.savePosition();
      };
      const timer = setTimeout(finish, Math.max(0, 10000 - (performance.now() - started)));
      let resizeObserver = null;
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(scheduleCorrection);
        resizeObserver.observe(grid);
        resizeObserver.observe(document.documentElement);
      }
      window.addEventListener('resize', scheduleCorrection);
      window.addEventListener('load', scheduleCorrection);
      window.addEventListener('pageshow', scheduleCorrection);
      document.addEventListener('load', scheduleCorrection, true);
      document.fonts?.addEventListener?.('loadingdone', scheduleCorrection);
      this.positionGuard = () => {
        clearTimeout(timer);
        if (frame !== null) cancelAnimationFrame(frame);
        resizeObserver?.disconnect();
        window.removeEventListener('resize', scheduleCorrection);
        window.removeEventListener('load', scheduleCorrection);
        window.removeEventListener('pageshow', scheduleCorrection);
        document.removeEventListener('load', scheduleCorrection, true);
        document.fonts?.removeEventListener?.('loadingdone', scheduleCorrection);
      };
      document.fonts?.ready?.then(() => { if (this.positionGuard && stillCurrent()) scheduleCorrection(); });
      this.updateControls(this.autoBlocked);
      this.observe();
      document.dispatchEvent(new CustomEvent('faf:collection-position-restored', { detail: { page: this.page, root: grid } }));
      return true;
    }
  }

  window.FAFCollectionListing = new CollectionListing();
})();

// Existing theme filters call these functions after replacing the collection grid.
function loadMore() { window.FAFCollectionListing?.mount(); }
function loadItems(button) { return window.FAFCollectionListing?.loadNext(button, false); }
function checkVisibility() { window.FAFCollectionListing?.observe(); }
